import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Button, Drawer } from "antd";
import {
  NotificationOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  HomeOutlined,
  UserOutlined,
  CheckSquareOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  InfoCircleOutlined,
  UnlockOutlined,
  ProfileOutlined,
  MenuOutlined,
  CloseOutlined,
  IdcardOutlined, // for Entry Pass
  SecurityScanOutlined, // for Guard List
} from "@ant-design/icons";
import { PiNotebookFill } from "react-icons/pi";
import { FaFileInvoice } from "react-icons/fa";
import { MdDashboardCustomize } from "react-icons/md";

const { Sider, Content } = Layout;

const Admindash = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();

  // Get user role from localStorage (assumes you store user object with role)
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = user.role_name?.[0] ;
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setDrawerVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    const confirmLogout = window.confirm("Are you sure you want to log out?");
    if (confirmLogout) {
      localStorage.removeItem("user");
      navigate("/");
    }
  };

  const currentPath = location.pathname;
  const selectedKey = currentPath.split("/").pop() || "dashboard";

  // Base menu items visible to admin
  const adminMenuItems = [
    {
      key: "dashboard",
      icon: <MdDashboardCustomize style={{ fontSize: "18px" }} />,
      label: "Dashboard",
      link: "/dashboard",
    },
    {
      key: "noticelist",
      icon: <PiNotebookFill style={{ fontSize: "18px" }} />,
      label: "Notices",
      link: "noticelist",
    },
    {
      key: "paymentlist",
      icon: <FaFileInvoice style={{ fontSize: "18px" }} />,
      label: "Create Bill",
      link: "paymentlist",
    },
    {
      key: "confirmpayment",
      icon: <CreditCardOutlined style={{ fontSize: "18px" }} />,
      label: "Manage Payment",
      link: "confirmpayment",
    },
    {
      key: "booking",
      icon: <CheckSquareOutlined style={{ fontSize: "18px" }} />,
      label: "Community Hall Booking",
      link: "booking",
    },
    {
      key: "eventlist",
      icon: <CalendarOutlined style={{ fontSize: "18px" }} />,
      label: "Events",
      link: "eventlist",
    },
    {
      key: "manage-users",
      icon: <TeamOutlined style={{ fontSize: "18px", color:"white" }} />,
      label: "Manage Users",
      children: [
        {
          key: "userlist",
          icon: <HomeOutlined style={{ fontSize: "16px" }} />,
          label: "Resident",
          link: "userlist",
        },
        {
          key: "workerlist",
          icon: <UserOutlined style={{ fontSize: "16px" }} />,
          label: "Staffs",
          link: "workerlist",
        },
        {
          key: "guard-list",
          icon: <SecurityScanOutlined style={{ fontSize: "16px" }} />,
          label: "Guard List",
          link: "guard-list",
        },
      ],
    },
    {
      key: "guard",
      icon: <IdcardOutlined style={{ fontSize: "18px" }} />,
      label: "Entry Pass",
      link: "guard",
    },
    {
      key: "attendance",
      icon: <UserOutlined style={{ fontSize: "18px"  }} />,
      label: "Attendance",
      link: "attendance",
    },
    {
      key: "settings",
      icon: <SettingOutlined style={{ fontSize: "18px" , color:"white"}}  />,
      label: "Settings",
      children: [
        {
          key: "invoicesettings",
          icon: <InfoCircleOutlined style={{ fontSize: "16px" }} />,
          label: "Invoice Item Setting",
          link: "invoicesettings",
        },
        {
          key: "housesettings",
          label: "House Setting",
          icon: <HomeOutlined style={{ fontSize: "16px" }} />,
          link: "housesettings",
        },
        {
          key: "changepassword",
          icon: <UnlockOutlined style={{ fontSize: "16px" }} />,
          label: "Change Password",
          link: "changepassword",
        },
        {
          key: "editprofile",
          icon: <ProfileOutlined style={{ fontSize: "16px" }} />,
          label: "Your Profile",
          link: "editprofile",
        },
      ],
    },
  ];

  // Guard-only menu (minimal access)
  const guardMenuItems = [
    {
      key: "guard",
      icon: <IdcardOutlined style={{ fontSize: "18px" }} />,
      label: "Entry Pass",
      link: "guard",
    },
    {
          key: "editprofile",
          icon: <ProfileOutlined style={{ fontSize: "16px" }} />,
          label: "Your Profile",
          link: "editprofile",
        },
  ];

  // Choose menu based on role
  const menuItems = userRole === "guard" ? guardMenuItems : adminMenuItems;

  const renderMenuItems = (items, inDrawer = false) => {
    return items.map(({ key, icon, label, link, children }) =>
      children ? (
        <Menu.SubMenu
          key={key}
          icon={icon}
          title={<span style={{ color: "white" }}>{label}</span>}
        >
          {children.map((subItem) => (
            <Menu.Item
              key={subItem.key}
              icon={subItem.icon}
              style={{
                color: selectedKey === subItem.key ? "#1890ff" : "white",
                backgroundColor: selectedKey === subItem.key ? "rgba(24, 144, 255, 0.1)" : "transparent",
              }}
              onClick={() => inDrawer && setDrawerVisible(false)}
            >
              <Link to={subItem.link}>{subItem.label}</Link>
            </Menu.Item>
          ))}
        </Menu.SubMenu>
      ) : (
        <Menu.Item
          key={key}
          icon={icon}
          style={{
            color: selectedKey === key ? "#1890ff" : "white",
            backgroundColor: selectedKey === key ? "rgba(24, 144, 255, 0.1)" : "transparent",
          }}
          onClick={() => inDrawer && setDrawerVisible(false)}
        >
          <Link to={link}>
            <span style={{ color: "white" }}>{label}</span>
          </Link>
        </Menu.Item>
      )
    );
  };

  return (
    <Layout style={{ minHeight: "100vh", backgroundColor: "#3F3F95" }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={250}
          theme="light"
          breakpoint="md"
          collapsedWidth={80}
          style={{
            backgroundColor: "#3F3F95",
            height: "100vh",
            overflowY: "auto",
            position: "fixed",
            left: 0,
            zIndex: 1000,
          }}
          className="no-scrollbar"
        >
          <div className="px-10 py-4">
            {!collapsed && (
              <img
                src="../assets/images/image.jpeg"
                alt="logo"
                className="rounded-full w-32 h-32 mx-auto"
              />
            )}
          </div>
          <p className="text-white font-bold text-lg text-center pb-4">
            {!collapsed ? "TCH II Thaiba" : "TCH"}
          </p>
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            style={{
              height: "100%",
              borderRight: 0,
              backgroundColor: "#3F3F95",
            }}
          >
            {renderMenuItems(menuItems)}
            <Menu.Item
              key="logout"
              icon={<LogoutOutlined style={{ fontSize: "18px" }} />}
              onClick={handleLogout}
              style={{ color: "white", marginTop: "auto" }}
            >
              <span style={{ color: "white" }}>Logout</span>
            </Menu.Item>
          </Menu>
        </Sider>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 bg-[#3F3F95] z-50 flex justify-between items-center px-4 py-3 shadow-lg">
          <div className="flex items-center gap-2">
            <img
              src="../assets/images/image.jpeg"
              alt="logo"
              className="rounded-full w-10 h-10"
            />
            <span className="text-white font-bold text-base">TCH II Thaiba</span>
          </div>
          <div className="flex gap-2">
            <Button
              type="text"
              icon={<LogoutOutlined style={{ color: "white", fontSize: "20px" }} />}
              onClick={handleLogout}
            />
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: "white", fontSize: "20px" }} />}
              onClick={() => setDrawerVisible(true)}
            />
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      <Drawer
        title={
          <div className="flex items-center gap-2">
            <img
              src="../assets/images/image.jpeg"
              alt="logo"
              className="rounded-full w-10 h-10"
            />
            <span className="text-white font-bold">TCH II Thaiba</span>
          </div>
        }
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={280}
        headerStyle={{ backgroundColor: "#3F3F95", borderBottom: "1px solid rgba(255,255,255,0.1)" }}
        bodyStyle={{ backgroundColor: "#3F3F95", padding: 0 }}
        closeIcon={<CloseOutlined style={{ color: "white" }} />}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ backgroundColor: "#3F3F95", border: "none" }}
        >
          {renderMenuItems(menuItems, true)}
          <Menu.Item
            key="logout"
            icon={<LogoutOutlined style={{ fontSize: "18px" }} />}
            onClick={() => {
              setDrawerVisible(false);
              handleLogout();
            }}
            style={{ color: "white" }}
          >
            <span style={{ color: "white" }}>Logout</span>
          </Menu.Item>
        </Menu>
      </Drawer>

      {/* Main Content Area */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 80 : 250,
          marginTop: isMobile ? 56 : 0,
          transition: "margin 0.2s",
        }}
      >
        <Content
          style={{
            padding: 24,
            backgroundColor: "#f0f2f5",
            minHeight: "calc(100vh - 56px)",
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default Admindash;